import plantsClient from '@/core/services/plants';
import { isCodeCorrectValue, KeypadCodeValidation, keypadCodeValidationToString } from '@/core/utils/EntrancePlant/entrance-plant.utils';
import { Options, Vue } from 'vue-class-component';

@Options({
  name: 'KeypadCodeInput',
  props: {
    modelValue: {
      type: String,
      required: true,
    },
    keypadCodeRequiredLength: {
      type: Number,
      required: true,
    },
    fullWidth: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: false,
    },
    labelId: {
      type: Number,
      required: false,
      default: 0,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    alreadyUsedCodes: {
      type: Array,
      required: true,
      default: [],
    },
    drawAttention: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  watch: {
    keypadCode: function (value: string, oldValue: string) {
      if (value !== oldValue && this.keypadCodeEdited()) {
        this.resetConfirmValidation();
      }
      this.codeWasEdited();
    },
    codeCheck: function () {
      this.codeWasEdited();
    },
    drawAttention: function () {
      this.doDrawAttention();
    }
  },
  emits: ['key-code-edited', 'error-key-code', 'update:modelValue'],
})
export default class KeypadCodeInput extends Vue {
  modelValue!: string;
  keypadCodeRequiredLength!: number;
  fullWidth!: boolean;
  label!: string;
  labelId!: number;
  alreadyUsedCodes!: string[];
  drawAttention!: boolean;
  codeCheck: string = '';
  codeConfirmationNotValid = false;
  codeConfirmationValid = false;
  newCodeNotValid = false;
  oldKeypadCode = '';
  disabled!: boolean;
  invalidNewCodeMessage = '';

  mounted() {
    if (this.modelValue) {
      this.oldKeypadCode = this.modelValue;
      this.keypadCode = this.modelValue;
      this.codeCheck = this.modelValue;
    }
    if (this.drawAttention) {
      this.doDrawAttention();
    }
  }

  get keypadCode() {
    return this.modelValue;
  }

  set keypadCode(v: any) {
    this.$emit('update:modelValue', v);
  }

  get usedCodes() {
    return this.alreadyUsedCodes.filter((code) => code !== this.oldKeypadCode);
  }

  get isCorrectValue() {
    if (this.keypadCode === '') {
      return true;
    }
    const validation = isCodeCorrectValue(this.keypadCode, this.keypadCodeRequiredLength, this.usedCodes);
    this.invalidNewCodeMessage = keypadCodeValidationToString(validation);
    return validation === KeypadCodeValidation.OK;
  }

  get enableShare() {
    return this.codeCheck !== '' && this.codeCheck === this.keypadCode;
  }

  get codeInputHasError() {
    const errorCodeValidation = !this.isCorrectValue || (this.codeCheck !== this.keypadCode);
    return errorCodeValidation;
  }

  doDrawAttention() {
    this.newCodeNotValid = this.drawAttention;
    this.invalidNewCodeMessage = this.drawAttention ? this.$t('AppResourceEP.KeypadCodePlaceHolder') : '';
  }

  codeWasEdited() {
    this.$emit('error-key-code', this.codeInputHasError);
    this.$emit('key-code-edited', this.keypadCodeEdited(), this.keypadCode, this.labelId);
  }

  checkNewCodeValidation() {
    this.newCodeNotValid = this.keypadCode !== '' && !this.isCorrectValue;
  }

  resetNewCodeValidation() {
    this.newCodeNotValid = false;
    this.codeConfirmationNotValid = false;
  }

  checkCodeConfirmationValid() {
    if (this.codeCheck.trim() !== '' || this.keypadCode.trim() !== '') {
      this.codeConfirmationNotValid = this.codeCheck !== this.keypadCode;
      this.codeConfirmationValid = this.isCorrectValue && this.codeCheck === this.keypadCode;
    }
  }

  resetConfirmValidation() {
    this.codeConfirmationNotValid = false;
    this.codeConfirmationValid = false;
  }

  keypadCodeEdited() {
    if (this.keypadCode !== '') {
      return this.codeCheck === this.keypadCode && this.keypadCode !== this.oldKeypadCode;
    } else {
      return this.keypadCode !== this.oldKeypadCode;
    }
  }

  async codeGenerator() {
    this.resetConfirmValidation();
    this.resetNewCodeValidation();
    let randStr = '';
    let i = 0;
    do {
      randStr = await plantsClient.randomkeypadcode(this.keypadCodeRequiredLength);
      i++;
    } while (this.usedCodes.includes(randStr.toString()) && i < 50);

    if (i >= 50) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('AppResource.TitleError'),
        life: 3000,
      });
      return;
    }

    this.keypadCode = randStr.toString();
    this.codeCheck = randStr.toString();
  }
}
